import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';
import { AnalyticsService } from '../../services/analytics.service';

/**
 * 🔐 FORGOT PASSWORD COMPONENT
 *
 * FLOW:
 * 1. Utilisateur entre son email
 * 2. Composant valide l'email
 * 3. Envoie requête POST /api/auth/forgot-password
 * 4. Backend génère token et envoie email
 * 5. Affiche message de confirmation (générique pour sécurité)
 *
 * SÉCURITÉ:
 * - Réponse générique (ne révèle pas si email existe)
 * - Rate limiting côté backend
 * - Message ne contient pas d'infos sensibles
 */
@Component({
  selector: 'app-forgot-password',
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css'],
})
export class ForgotPasswordComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  forgotPasswordForm: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  emailSubmitted: string | null = null;

  constructor() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.log.debug('🔐 ForgotPasswordComponent initialized');
  }

  /**
   * 📧 Soumet la demande de réinitialisation
   *
   * FLOW:
   * 1. Valide le formulaire
   * 2. Appelle authService.forgotPassword(email)
   * 3. Affiche message générique de succès
   * 4. Stocke email pour afficher les instructions
   */
  onSubmit(): void {
    this.log.debug('🔐 Submitting forgot password form');

    if (this.forgotPasswordForm.invalid) {
      this.log.debug('⚠️ Form invalid, marking as touched');
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;

    const { email } = this.forgotPasswordForm.value;
    this.log.debug(`🔐 Calling forgotPassword for email: ${email}`);

    this.authService.forgotPassword(email).subscribe({
      next: (response) => {
        this.log.debug('✅ Forgot password request succeeded');
        this.analytics.trackMotDePasseOublie();
        // 🔐 SÉCURITÉ: Message générique, ne révèle pas si email existe
        this.successMessage =
          'Si un compte avec cet email existe, vous recevrez un lien de réinitialisation dans quelques instants.';
        this.emailSubmitted = email;
        this.forgotPasswordForm.reset();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('❌ Forgot password error:', err);
        // 🔐 SÉCURITÉ: Message générique même en cas d'erreur
        this.successMessage =
          'Si un compte avec cet email existe, vous recevrez un lien de réinitialisation dans quelques instants.';
        this.emailSubmitted = email;
        this.forgotPasswordForm.reset();
        this.cdr.markForCheck();
      },
      complete: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * 🔄 Retour à la page de connexion
   */
  goToLogin(): void {
    this.log.debug('🔄 Navigating to login');
    this.router.navigate(['/login']);
  }
}
